import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { theme } from '../theme';
import { LogoCompact } from '../Icons';
import AccountScreenBanner from './AccountScreenBanner';

// EmailChangeScreen — new (Sep 7, per Ali's report — Phase 2 of the
// account-settings menu). Mirrors ResetPasswordScreen.js's structure
// (login-card, login-input-wrap, btn-primary login-submit-btn)
// deliberately, same reasoning as that file: reads as the same
// product, not a bolted-on utility screen.
//
// Uses Supabase Auth's own updateUser({ email }) rather than a custom
// backend endpoint — this was a real decision, not a shortcut: Supabase
// already sends a confirmation link to the NEW address and only
// applies the change once that link is clicked, which is exactly the
// "verify before it takes effect" behavior Ali asked for, built in
// natively. A custom backend endpoint would just be reimplementing
// that same flow with more surface area to get wrong.
function EmailChangeScreen() {
  const navigate = useNavigate();
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email) setCurrentEmail(data.user.email);
    });
  }, []);

  const handleSubmit = async () => {
    setError(null);

    if (!newEmail || !newEmail.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    if (newEmail === currentEmail) {
      setError("That's already your current email.");
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ email: newEmail });
    setSubmitting(false);

    if (updateError) {
      setError(updateError.message || 'Could not update your email. Try again.');
      return;
    }

    setDone(true);
  };

  return (
    <>
      <AccountScreenBanner section="Change Email" />
      <div className="login-form-panel" style={{ minHeight: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="login-card yedge">
          <div className="login-card-body">
          <div className="login-card-icon">
            <LogoCompact width={150} color="#1E3A5F" />
          </div>
          <h2 style={{ textAlign: 'center', margin: '0 0 4px', color: theme.textPrimary, fontFamily: theme.fontDisplay }}>
            Change your email
          </h2>
          <p style={{ textAlign: 'center', color: theme.textMuted, margin: '0 0 24px' }}>
            {done ? "Almost there." : 'A confirmation link goes to your new address before this takes effect.'}
          </p>

          {!done ? (
            <>
              {currentEmail && (
                <p style={{ fontSize: '12.5px', color: theme.textMuted, marginBottom: '18px' }}>
                  Current email: <b style={{ color: theme.textPrimary }}>{currentEmail}</b>
                </p>
              )}

              <label>New email</label>
              <div className="login-input-wrap">
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>

              {error && (
                <p style={{ fontSize: '12.5px', color: theme.danger, marginTop: '8px', marginBottom: 0, fontWeight: 600 }}>{error}</p>
              )}

              <button
                className="btn-primary login-submit-btn"
                style={{ marginTop: '20px', width: '100%' }}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Sending…' : 'Send confirmation link'}
              </button>
              <p
                onClick={() => navigate('/dashboard')}
                style={{ textAlign: 'center', fontSize: '12.5px', color: theme.textMuted, marginTop: '16px', cursor: 'pointer' }}
              >
                Cancel
              </p>
            </>
          ) : (
            <p style={{ textAlign: 'center', fontSize: '13px', color: theme.textPrimary, lineHeight: 1.6 }}>
              Check <b>{newEmail}</b> for a confirmation link. Your email won't change until you click it — until then, keep signing in with {currentEmail}.
            </p>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default EmailChangeScreen;